import type { HTMLAttributes } from 'react';
import { cn } from '../cn.js';
import { initials } from '../initials.js';
import { avatarColorClasses } from '../avatarColor.js';

type Size = 'sm' | 'md' | 'lg';

const SIZES: Record<Size, string> = {
  sm: 'size-7 text-[11px]',
  md: 'size-9 text-xs',
  lg: 'size-11 text-sm',
};

export interface AvatarProps extends HTMLAttributes<HTMLDivElement> {
  name: string;
  imageUrl?: string | null;
  size?: Size;
  /** Renders on the hero gradient rather than a plain surface — see Avatar.onHero usage. */
  onHero?: boolean;
  /** Stabilises the colour bucket independently of `name` — pass the person's id when the caller
   *  has one. Two people who happen to share a display name would otherwise land on the same
   *  colour; a stable id keeps every person visually distinct. Falls back to `name` when omitted,
   *  which is still deterministic (same person, same name, same colour every render) — just not
   *  collision-proof against a duplicate name. */
  colorSeed?: string;
}

/**
 * Name-initials avatar, photo optional.
 *
 * Most employees never upload a photo, so initials are the default rendering rather than a
 * fallback for a broken image — this is the same logic ApprovalCard already used for its
 * requester avatar, now shared so a directory listing and a hero card agree on one employee's
 * initials.
 *
 * The initials circle is coloured from a fixed 8-hue categorical palette (`--avatar-*`), picked
 * deterministically from `colorSeed`/`name` — a list of several people reads as distinct
 * individuals at a glance instead of a wall of identical grey circles. `onHero` keeps its own
 * translucent-white treatment instead (a categorical colour would compete with the gradient it
 * sits on, and there the surrounding hero card already establishes whose profile this is).
 */
export function Avatar({
  name,
  imageUrl,
  size = 'md',
  onHero = false,
  colorSeed,
  className,
  ...rest
}: AvatarProps) {
  if (imageUrl) {
    return (
      <img
        src={imageUrl}
        alt=""
        loading="lazy"
        className={cn('rounded-full object-cover', SIZES[size], className)}
      />
    );
  }

  return (
    <div
      className={cn(
        'flex items-center justify-center rounded-full font-display font-semibold',
        onHero
          ? 'border border-white/40 bg-white/20 text-white'
          : avatarColorClasses(colorSeed || name),
        SIZES[size],
        className,
      )}
      aria-hidden="true"
      {...rest}
    >
      {initials(name)}
    </div>
  );
}
